// Pure, UI-free helpers for the Clientes screen and the booking customer picker. The SPA test setup has no
// DOM library, so the logic worth testing lives here and the components stay thin wrappers around it.
import { preCleanPhone } from './phone';

export function consentLabel(state: string | null): string {
  switch (state) {
    case 'Granted':
      return 'Consentimento concedido';
    case 'Withdrawn':
      return 'Consentimento retirado';
    default:
      return 'Sem registo de consentimento';
  }
}

/** The consent history comes newest-first from the API, so the current state is its first element (or none). */
export function currentConsent(history: readonly { state: string }[]): string | null {
  return history.length > 0 ? history[0].state : null;
}

export function identityKindLabel(kind: string): string {
  switch (kind) {
    case 'Email':
      return 'Email';
    case 'Phone':
      return 'Telefone';
    default:
      return kind;
  }
}

export interface Searchable {
  displayName: string;
  identities: readonly { value: string; kind: string }[];
}

const SEPARATORS = /[\s()+.-]/g; // '-' last so it is a literal, not a range

// Fold a phone the way the API stores it (shared preCleanPhone: whitespace + marks + "(0)" trunk), then strip
// separators, so a search for "912 000", "+49 (0) 170…" or "+49.170…" matches the stored normalised number.
function phoneKey(value: string): string {
  return preCleanPhone(value).replace(SEPARATORS, '');
}

export function matchesQuery(customer: Searchable, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') {
    return true;
  }
  if (customer.displayName.toLowerCase().includes(q)) {
    return true;
  }
  const qStripped = phoneKey(q);
  return customer.identities.some((identity) => {
    const value = identity.value.toLowerCase();
    if (value.includes(q)) {
      return true;
    }
    // Phone-fold ONLY phone identities, decided by kind (not by guessing from '@'): an email keeps its dots,
    // so a dotted address can never over-match a digit run.
    return identity.kind === 'Phone' && qStripped !== '' && phoneKey(value).includes(qStripped);
  });
}

export function filterCustomers<T extends Searchable>(customers: readonly T[], query: string): T[] {
  return customers.filter((customer) => matchesQuery(customer, query));
}

export function bookingStatusLabel(status: string): string {
  switch (status) {
    case 'Confirmed':
      return 'Confirmada';
    case 'Pending':
      return 'Pendente';
    case 'Cancelled':
      return 'Cancelada';
    default:
      return status;
  }
}

export function erasureStatusLabel(status: string): string {
  return status === 'Requested' ? 'Pedido registado' : status;
}
